import { ClipboardCheck, Flag, ShieldCheck } from "@tamagui/lucide-icons";
import { Image } from "expo-image";
import { useContext } from "react";
import { View, XStack, YStack } from "tamagui";

import DavisImage from "@/assets/images/landing_page/general_care/davis.png";
import { Grid, GridItem, getGridLayout } from "@/components";
import { LayoutContext } from "@/components/LayoutProvider";
import { Text } from "@/components/Typography";
import { medicalGroupName } from "@/config/brand";
import LandingGradientBackground from "./LandingGradientBackground";

type StatItem = {
  value: string;
  label: string;
};

const STATS: StatItem[] = [
  { value: "2019", label: "Clinic founded" },
  { value: "1M+", label: "Virtual visits" },
  { value: "4.5 ★", label: "Average review" },
];

type TrustItem = {
  Icon: React.ComponentType<{ size?: number; color?: string }>;
  label: string;
};

const STAT_GRID_LAYOUT = getGridLayout(2);

const TRUST_ITEMS: TrustItem[] = [
  { Icon: Flag, label: "Available nationwide." },
  { Icon: ClipboardCheck, label: "Every clinician is board-certified." },
  { Icon: ShieldCheck, label: "HIPAA compliant and SOC2 certified." },
];

function StatBlock({ stat }: { stat: StatItem }) {
  return (
    <YStack gap="$50">
      <Text size="$xxl" semibold>
        {stat.value}
      </Text>
      <Text size="$sm" color="$textSecondary">
        {stat.label}
      </Text>
    </YStack>
  );
}

function TrustCard({ item }: { item: TrustItem }) {
  const { Icon, label } = item;

  return (
    <XStack
      backgroundColor="$backgroundGround"
      borderRadius="$200"
      borderWidth="$strokeWidth"
      borderColor="$strokeSubdued"
      paddingHorizontal="$300"
      paddingVertical="$250"
      alignItems="center"
      gap="$300"
    >
      <View
        backgroundColor="$strokeSubdued"
        borderRadius="$3000"
        padding="$150"
        flexShrink={0}
      >
        <Icon size={24} color="$iconBase" />
      </View>
      <Text size="$sm">{label}</Text>
    </XStack>
  );
}

export default function MedicalGroupTrust() {
  const { lg } = useContext(LayoutContext);
  return (
    <LandingGradientBackground
      darkColor="var(--tertiary900)"
      lightColor="var(--tertiary50)"
      lightStartPct="30%"
    >
      <YStack
        maxWidth="$landingPageConstraint"
        alignSelf="center"
        paddingHorizontal="$400"
        gap="$800"
        justifyContent="center"
        paddingVertical="$400"
        width="100%"
      >
        <YStack alignItems="center" gap="$100">
          <Text size="$xxl" textAlign="center">
            Backed by the {medicalGroupName}
          </Text>
          <Text size="$md" textAlign="center" color="$textSecondary">
            Real physicians and nurse practitioners, licensed in every state,
            behind every visit.
          </Text>
        </YStack>

        <XStack
          width="100%"
          gap="$500"
          flexDirection={lg ? "row" : "column"}
          alignItems={lg ? "flex-start" : "stretch"}
        >
          <YStack flex={lg ? 2 : undefined} gap="$500">
            <Grid justifyContent="center">
              {STATS.map((stat) => (
                <YStack
                  key={stat.label}
                  {...STAT_GRID_LAYOUT}
                  $lg={{ flex: 1, width: "auto" }}
                  alignItems="center"
                >
                  <StatBlock stat={stat} />
                </YStack>
              ))}
            </Grid>

            <Grid>
              {TRUST_ITEMS.map((item) => (
                <GridItem key={item.label}>
                  <TrustCard item={item} />
                </GridItem>
              ))}
            </Grid>
          </YStack>

          <YStack flex={1} gap="$300">
            <View
              borderRadius="$300"
              overflow="hidden"
              height={260}
              width="100%"
            >
              <Image
                source={DavisImage}
                style={{ height: "100%", width: "100%" }}
                contentFit="cover"
                contentPosition="top center"
              />
            </View>
            <YStack>
              <Text semibold>Dr. Davis Liu, MD</Text>
              <Text size="$sm" color="$textSecondary">
                Chief Medical Officer, Curai Health
              </Text>
            </YStack>
            <Text size="$sm" color="$textTertiary" fontStyle="italic">
              Previously at Lemonaid Health & Kaiser Permanente
            </Text>
          </YStack>
        </XStack>
      </YStack>
    </LandingGradientBackground>
  );
}
